import EmojiPicker, { Theme, EmojiClickData } from 'emoji-picker-react';
import { Emojis } from './styles';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMobileDevice } from '../../global/useMobile';

interface Props {
  onEmojiClick: (emojiData: EmojiClickData) => void;
  emojiButtonRef?: React.RefObject<HTMLDivElement | null>;
  isOpenEmoji: boolean;
  setIsOpenEmoji: (open: boolean) => void;
  setOpenDropdownId?: (id: string | null) => void;
  isMessage?: boolean;
  isEmojiLoading?: boolean;
  isCommentModal?: boolean; // <- new prop
  isEmojiModal?: boolean;
}

const EmojiPickerDropdown = ({
  onEmojiClick,
  emojiButtonRef,
  isOpenEmoji,
  setIsOpenEmoji,
  setOpenDropdownId,
  isMessage,
  isEmojiLoading = false,
  isCommentModal = false,
  isEmojiModal = false
}: Props) => {
  const emojiDropdownRef = useRef<HTMLDivElement | null>(null);
  const [coordinates, setCoordinates] = useState({ top: 0, left: 0 });
  const isMobile = useMobileDevice();

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    onEmojiClick(emojiData);
    if (isMobile) {
      setIsOpenEmoji(false);
      setOpenDropdownId?.(null);
    }
  };

  // Prevent wheel/touch events from reaching the document
  useEffect(() => {
    if (!isOpenEmoji) return;

    const preventDefault = (e: Event) => {
      if (!emojiDropdownRef.current?.contains(e.target as Node)) {
        e.preventDefault();
      }
    };

    document.addEventListener('wheel', preventDefault, { passive: false });
    document.addEventListener('touchmove', preventDefault, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventDefault);
      document.removeEventListener('touchmove', preventDefault);
    };
  }, [isOpenEmoji]);

  useLayoutEffect(() => {
    if (isOpenEmoji && emojiButtonRef?.current && emojiDropdownRef?.current) {
      const buttonRect = emojiButtonRef.current.getBoundingClientRect();
      const dropdownRect = emojiDropdownRef.current.getBoundingClientRect();

      const VIEWPORT_PADDING = 16;
      const MESSAGE_BOTTOM_OFFSET = isMessage ? 85 : 0;

      // Calculate available space
      const spaceBelow = window.innerHeight - buttonRect.bottom - MESSAGE_BOTTOM_OFFSET;
      const spaceAbove = buttonRect.top;

      // Determine vertical position
      let top = 0;
      if (spaceBelow >= dropdownRect.height + VIEWPORT_PADDING) {
        top = buttonRect.bottom + 5;
      } else if (spaceAbove >= dropdownRect.height + VIEWPORT_PADDING) {
        top = buttonRect.top - dropdownRect.height - 5;
      } else {
        top = spaceBelow > spaceAbove ? buttonRect.bottom + 5 : buttonRect.top - dropdownRect.height - 5;
      }

      // Determine horizontal position
      let left = buttonRect.left;
      if (buttonRect.left + dropdownRect.width > window.innerWidth - VIEWPORT_PADDING) {
        left = window.innerWidth - dropdownRect.width - VIEWPORT_PADDING;
      }

      // Ensure within viewport bounds
      top = Math.max(VIEWPORT_PADDING, Math.min(top, window.innerHeight - dropdownRect.height - VIEWPORT_PADDING));
      left = Math.max(VIEWPORT_PADDING, left);

      setCoordinates({ top, left });
    }
  }, [isOpenEmoji, emojiButtonRef, isMessage]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !emojiDropdownRef.current?.contains(event.target as Node) &&
        !emojiButtonRef?.current?.contains(event.target as Node)
      ) {
        setIsOpenEmoji(false);
        setOpenDropdownId?.(null);
      }
    };

    if (isOpenEmoji) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpenEmoji, emojiButtonRef, setIsOpenEmoji, setOpenDropdownId]);

  return (
    <Emojis
      role='button'
      ref={emojiDropdownRef}
      style={
        isCommentModal || isEmojiModal
          ? undefined
          : {
              position: 'fixed',
              top: `${coordinates.top}px`,
              left: `${coordinates.left}px`,
              overflow: 'hidden'
            }
      }
      onClick={(e) => {
        e.stopPropagation();
      }}
      isEmojiLoading={isEmojiLoading}
      isCommentModal={isCommentModal}
      isEmojiModal={isEmojiModal}>
      <EmojiPicker
        searchPlaceholder='Search Emoji'
        theme={Theme.AUTO}
        width={258}
        height={240}
        onEmojiClick={handleEmojiClick}
        lazyLoadEmojis={true}
      />
    </Emojis>
  );
};

export default EmojiPickerDropdown;
